import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { BUSINESS, LOGO_URL } from "@/lib/api";

const links = [
  { href: "#services", label: "Services" },
  { href: "#why", label: "Why Drones" },
  { href: "#process", label: "Process" },
  { href: "#crops", label: "Crops" },
  { href: "#gallery", label: "Gallery" },
  { href: "#faq", label: "FAQ" },
  { href: "#contact", label: "Contact" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled ? "glass border-b border-border/60" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 md:px-8 h-[188px] flex items-center justify-between">
        <Link to="/" data-testid="brand-logo" className="flex items-center group">
          <img
            src={LOGO_URL}
            alt="AeroAcres Drone Spraying logo"
            className="h-[140px] md:h-[172px] w-auto object-contain"
            loading="eager"
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              className="px-3.5 py-2 text-sm font-medium text-primary/80 hover:text-primary rounded-full hover:bg-muted transition-colors"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <a
            href={`tel:${BUSINESS.phoneRaw}`}
            data-testid="header-call-btn"
            data-conversion="phone-call"
            className="track-phone-click hidden lg:inline-flex items-center gap-2 text-sm font-semibold text-primary hover:text-accent transition-colors"
          >
            <Phone className="h-4 w-4" /> {BUSINESS.phone}
          </a>
          <Button asChild data-testid="header-quote-btn" className="rounded-full bg-accent hover:bg-accent/90 text-accent-foreground font-semibold px-5 h-10">
            <a href="#contact">Get a Quote</a>
          </Button>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          className="lg:hidden p-2 text-primary"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="lg:hidden glass border-t border-border">
          <nav className="px-5 py-4 flex flex-col gap-1">
            {links.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setOpen(false)}
                className="px-3 py-2.5 text-sm font-medium text-primary rounded-lg hover:bg-muted"
              >
                {l.label}
              </a>
            ))}
            <a
              href={`tel:${BUSINESS.phoneRaw}`}
              data-conversion="phone-call"
              className="track-phone-click mt-2 inline-flex items-center justify-center gap-2 h-11 rounded-full bg-primary text-primary-foreground font-semibold"
            >
              <Phone className="h-4 w-4" /> Call {BUSINESS.phone}
            </a>
          </nav>
        </div>
      )}
    </header>
  );
}
